import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { usePDF } from 'react-to-pdf';
import { DownloadTableExcel } from 'react-export-table-to-excel';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { formatCurrency } from '../util';
import { ItemsContext } from '../App';
import { generateCostSheetPdf } from '../pdfGenerator';
import { useMsal } from '@azure/msal-react';
import { getApiToken } from '../authUtils';
import { usePermissions } from '../rbac/PermissionsProvider';
import { PERMISSIONS } from '../rbac/permissions';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ProductSelectorModal from '../components/ProductSelectorModal'

import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
  Badge,
  TablePagination,
  Tooltip as MuiTooltip,
  Chip,
  Collapse,
  useMediaQuery,
  useTheme,
  Drawer,
  Autocomplete,
  Snackbar,
  LinearProgress,
} from '@mui/material';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuIcon from '@mui/icons-material/Menu';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BrushIcon from '@mui/icons-material/Brush';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import DeliveriesTable from "../components/DeliveriesTable";
import CostSheetHistory from '../components/CostSheetHistory';
import ProductCatalogManager from '../components/ProductCatalogManager';
import ExportAlldata from '../components/ExportAlldata';
import ExportByDate from '../components/ExportByDate';

import { styled, alpha, keyframes } from "@mui/material/styles";
import SearchInput from '../components/SearchInput';
import LineItemsTable, { isInternalSwitch } from '../shared/LineItemsTable';

const urlconfig = require('../config/urlconfig');

// ─── Status Colors for Table Chips ──────────────────────────────────────────────
const STATUS_COLORS = {
  'Delivered':                    { color: '#0284c7', bg: '#e0f2fe' },
  "PO's Released to vendors":     { color: '#65a30d', bg: '#ecfccb' },
  'Material Inward Done':         { color: '#ea580c', bg: '#ffedd5' },
  'Purchase Invoice Received':    { color: '#4b5563', bg: '#f3f4f6' },
  'Branding Completed':           { color: '#4f46e5', bg: '#e0e7ff' },
  'Material Dispatched':          { color: '#e11d48', bg: '#ffe4e6' },
  'Invoice Sent to client':       { color: '#0d9488', bg: '#ccfbf1' },
  'Pending':                      { color: '#d97706', bg: '#fef3c7' },
};

// ─── PP ranges with clear labels ───────────────────────────────────────────────
// Local calendar date as YYYY-MM-DD (toISOString would shift it to the UTC day)
const toIsoDate = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Values are sent to the server as pp:<value>; "a-b" means above a, up to and including b
const PP_RANGES = [
  { label: 'All', value: '' },
  { label: '₹0 – ₹230',        value: '<=230' },
  { label: '₹231 – ₹500',      value: '230-500' },
  { label: '₹501 – ₹1,000',    value: '500-1000' },
  { label: '₹1,001 – ₹2,000',  value: '1000-2000' },
  { label: '₹2,001 – ₹5,000',  value: '2000-5000' },
  { label: '₹5,001 – ₹10,000', value: '5000-10000' },
  { label: '₹10,001 – ₹20,000',value: '10000-20000' },
  { label: '₹20,001 – ₹50,000',value: '20000-50000' },
];

// ─── Animations & Styled Components ──────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
`;

const DashShell = styled(Box)({
  fontFamily: "'Sora','Nunito','system-ui',sans-serif",
  background: '#f8f9fc',
  minHeight: '100vh',
});

const StickyFilter = styled(Paper)(({ theme }) => ({
  position: 'sticky', top: 0, zIndex: 200,
  borderRadius: 0, borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5, 3),
  background: 'rgba(255,255,255,0.96)',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
}));

const KpiCard = styled(Paper)(({ theme }) => ({
  borderRadius: 18, padding: theme.spacing(2.5),
  background: '#fff',
  position: 'relative', overflow: 'hidden',
  cursor: 'pointer',
  transition: 'transform .18s, box-shadow .18s',
  animation: `${fadeUp} .4s ease both`,
  '&:hover': { transform: 'translateY(-3px)' },
  userSelect: 'none'
}));

const SectionWrap = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
}));

const StyledTableContainer = styled(TableContainer)({
  borderRadius: 12,
  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
  border: '1px solid #e2e8f0',
  marginBottom: '24px',
});

const thStyle = { fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.79rem', whiteSpace: 'nowrap' };

// ─── Helper Components ──────────────────────────────────────────────────────────
const NoData = ({ msg = 'No data matches the current filters.' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, bgcolor: '#f8fafc', borderRadius: 3, border: '1px dashed #cbd5e1', color: '#64748b' }}>
    <SearchOffIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
    <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No records found</Typography>
    <Typography variant="body2" color="text.disabled">{msg}</Typography>
  </Box>
);

// ─── Overdue accordion with data-quality warning ──────────────────────────────
const OverdueAccordion = ({ itemsArray, currentDate }) => {
  const [openDate, setOpenDate] = useState(null);

  const validOverdue = itemsArray.filter(item => {
    const d = new Date(item.deliveryDate);
    return d.getFullYear() > 1971 && !isNaN(d.getTime()) && d < currentDate && item.orderStatus !== 'Delivered';
  });
  const invalidDateItems = itemsArray.filter(item => {
    const d = new Date(item.deliveryDate);
    return (isNaN(d.getTime()) || d.getFullYear() <= 1971) && item.orderStatus !== 'Delivered';
  });

  const grouped = validOverdue.reduce((acc, item) => {
    const key = new Date(item.deliveryDate).toLocaleDateString('en-GB');
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {});

  const sorted = Object.entries(grouped).sort(([a], [b]) => {
    const toDate = s => new Date(s.split('/').reverse().join('-'));
    return toDate(b) - toDate(a);
  });

  const daysSince = (dateStr) => {
    const d = new Date(dateStr.split('/').reverse().join('-'));
    return Math.floor((currentDate - d) / 86400000);
  };

  const urgencyColor = (days) => days > 180 ? '#ef4444' : days > 30 ? '#f97316' : '#f59e0b';

  return (
    <Box>
      {invalidDateItems.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, borderRadius: 2, border: '1px solid #f59e0b' }}
        >
          <Typography variant="body2" fontWeight={700}>
            Data Quality Issue: {invalidDateItems.length} order{invalidDateItems.length !== 1 ? 's' : ''} have invalid delivery dates (stored as 1/1/1970)
          </Typography>
          <Typography variant="caption" display="block" mt={0.5}>
            These are likely null or zero timestamps saved incorrectly. Please correct the source data for these sales orders:{' '}
            {invalidDateItems.slice(0, 5).map(i => i.salesOrderNo).join(', ')}
            {invalidDateItems.length > 5 ? ` and ${invalidDateItems.length - 5} more…` : ''}
          </Typography>
        </Alert>
      )}

      {sorted.map(([date, deliveries]) => {
        const days = daysSince(date);
        const color = urgencyColor(days);
        const isOpen = openDate === date;
        return (
          <Paper
            key={date}
            variant="outlined"
            sx={{ mb: 1, borderRadius: 2, overflow: 'hidden', borderColor: isOpen ? color : '#e5e7eb' }}
          >
            <Box
              onClick={() => setOpenDate(p => p === date ? null : date)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: '12px 16px', cursor: 'pointer', background: isOpen ? `${color}08` : '#fff',
                '&:hover': { background: `${color}08` }, transition: 'background .15s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <Typography fontWeight={700} fontSize={14} color="#1f2937">{date}</Typography>
                <Typography fontSize={12} color="#6b7280">({deliveries.length} order{deliveries.length !== 1 ? 's' : ''})</Typography>
                <Chip
                  label={`${days}d overdue`}
                  size="small"
                  sx={{ fontSize: 11, fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}33`, height: 20 }}
                />
              </Box>
              {isOpen ? <ExpandLessIcon fontSize="small" sx={{ color: '#9ca3af' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: '#9ca3af' }} />}
            </Box>
            <Collapse in={isOpen}>
              <Box sx={{ borderTop: '1px solid #f3f4f6', background: '#fafafa', p: '8px 16px 12px' }}>
                {deliveries.map(d => (
                  <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                    <Box>
                      <Typography fontWeight={600} fontSize={13}>{d.salesOrderNo}</Typography>
                      <Typography fontSize={12} color="text.secondary">{d.clientName} · {d.scmspoc}</Typography>
                    </Box>
                    <Chip label={d.orderStatus} size="small" sx={{ fontSize: 11 }} />
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {sorted.length === 0 && !invalidDateItems.length && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography fontSize={36} mb={1}>🎉</Typography>
          <Typography fontWeight={700}>No overdue deliveries!</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Column visibility toggle ─────────────────────────────────────────────────
const ALL_COLUMNS = [
  'Date', 'Creation Date', 'Cost Sheet For', 'Client Name', 'Sales Order No',
  'SCM SPOC', 'Sales SPOC', 'Details', 'Brand', 'Category', 'Subcategory',
  'Delivery Date', 'Margin', 'Total PP', 'Total SP', 'PO Number',
  'Created By', 'Approval Status', 'Order Status',
];

const ColumnToggleMenu = ({ visibleCols, setVisibleCols }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={() => setOpen(o => !o)}
        sx={{ borderRadius: 2, fontSize: 12, fontWeight: 600, borderColor: '#e5e7eb', color: '#374151' }}
      >
        Columns ({visibleCols.length}/{ALL_COLUMNS.length})
      </Button>
      {open && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute', top: '110%', right: 0, zIndex: 50,
            minWidth: 220, p: 1.5, borderRadius: 2,
            border: '1px solid #e5e7eb', maxHeight: 340, overflowY: 'auto',
          }}
        >
          <Typography fontSize={11} fontWeight={700} color="text.secondary" sx={{ px: 0.5, pb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Toggle Columns
          </Typography>
          {ALL_COLUMNS.map(col => (
            <Box
              key={col}
              component="label"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.6, cursor: 'pointer', borderRadius: 1, '&:hover': { background: '#f3f4f6' }, fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={visibleCols.includes(col)}
                onChange={() => setVisibleCols(prev =>
                  prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
                )}
                style={{ accentColor: '#6366f1' }}
              />
              {col}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { items, deliveryItems, loading, error, refreshItems, fetchAllItemsForDeliveryAnalytics, legalClients, legalVendors, refreshLegalClientsAndVendors } = useContext(ItemsContext);
  const { instance, accounts } = useMsal();
  const account = accounts && accounts.length > 0 ? accounts[0] : null;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const { targetRef } = usePDF();
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [ppFilter, setPpFilter] = useState('');
  const [dateRange, setDateRange] = useState([{ startDate: null, endDate: null, key: 'selection' }]);
  const [DateModal, setDateModal] = useState(false);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [catalogTargetIndex, setCatalogTargetIndex] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);


  const [activeStatusCard, setActiveStatusCard] = useState(null);

  const [visibleCols, setVisibleCols] = useState([
    'Date', 'Creation Date', 'Cost Sheet For', 'Client Name', 'Sales Order No',
    'SCM SPOC', 'Sales SPOC', 'Details', 'Brand', 'Category', 'Subcategory',
    'Delivery Date', 'Margin', 'Total PP', 'Total SP', 'PO Number',
    'Created By', 'Approval Status', 'Order Status',
  ]);

  const [activeTab, setActiveTab] = useState('dashboard');

  const itemsArray = Array.isArray(deliveryItems.content) ? deliveryItems.content : [];
  const tableRef = useRef(null);
  const [deliveriesToShow, setDeliveriesToShow] = useState([]);
  const [tableTitle, setTableTitle] = useState('');
  const [tableColor, setTableColor] = useState('');
  const [startOffset, setStartOffset] = useState(0);
  const [direction, setDirection] = useState('next');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  //Admin Edit ----------------------------------------------------------
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [customVendorPaymentTerms, setCustomVendorPaymentTerms] = useState({});
  const [qtyErrors, setQtyErrors] = useState({});
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categorySubcategoryMap, setCategorySubcategoryMap] = useState({});
  const [catalogLoading, setCatalogLoading] = useState(false);

  const initialEditState = {
    _id: null,
    items: [{ details: '', customDetails: '', brand: null, category: null, subcategory: null, qty: '', gst: '', pp: '', nlc: '', sp: '', nsp: '', totalPp: '', totalSp: '', margin: '', marginPercent: '', vendorName: '', vendorPaymentTerms: '', poNumber: '' }],
    costsheetfor: '', createdBy: '', salesOrderNo: '', clientName: '',  legalClientId: null, salesPOC: '', scmspoc: '', orderStatus: 'Pending',
    pending: '', approvedBy: [], remarks: '', date: null, deliveryDate: null, userEmail: '', location: '',
    clientPaymentTerms: '', costSheetRemarks: '',
    transportationGST:'0', transportationTotalPp: '0', transportationTotalSp: '0', customizationGST:'0', customizationTotalPp: '0', customizationTotalSp: '0',
    marketingGST:'0', marketingTotalPp: '0', marketingTotalSp: '0', totalPP: '0.00', totalSP: '0.00', totalMargin: '0.00', totalMarginPercent: '0.00',
    internalcategory: '',
    customRows: [],
  };

  const [editItem, setEditItem] = useState(initialEditState);
  const [originalEditItem, setOriginalEditItem] = useState(null);
  const [originalCustomTerms, setOriginalCustomTerms] = useState({});

  const allowedCostsheetFor = [
      "eyantraventures",
      "eYantraIndustries",
      "Mozato"
  ];

  const filteredByDate = useMemo(() => {
  // if (!dateRange?.[0]?.startDate || !dateRange?.[0]?.endDate) return itemsArray;
    if (!dateRange?.[0]?.startDate || !dateRange?.[0]?.endDate) {
      return itemsArray.filter(item =>
        allowedCostsheetFor.includes(item.costsheetfor)
      );
  }

  const start = new Date(dateRange[0].startDate);
  const end = new Date(dateRange[0].endDate);
  end.setHours(23, 59, 59, 999);

  return itemsArray.filter(item => {
    //Switch date field based on tab
    const dateToCheck =
      activeTab === 'soAnalytics'
        ? new Date(item.createdAT)
        : new Date(item.deliveryDate);
    return (
      dateToCheck >= start &&
      dateToCheck <= end &&
      allowedCostsheetFor.includes(item.costsheetfor)
    );
  });
}, [itemsArray, dateRange, activeTab]);

  // ─── FETCH FIX: Removed context functions from dependencies ───────────────────
  // Search, filters and page are kept as they are, so the table can be reloaded in place after a save or delete
  const tableQueryRef = useRef('');
  const reloadTable = () => refreshItems(tableQueryRef.current, page, rowsPerPage);
  const [toast, setToast] = useState('');

  useEffect(() => {
    const filters = [];
    if (debouncedSearchQuery) filters.push(debouncedSearchQuery);
    if (orderStatusFilter !== 'all') filters.push(`orderStatus:${orderStatusFilter}`);
    if (ppFilter) filters.push(`pp:${ppFilter}`);
    const combinedSearchQuery = filters.join(' ');

    // The table's date range and status card are filtered on the server, so matches from every
    // page come back and the total count is right. Delivery analytics keeps its own date filter.
    const tableFilters = [];
    const { startDate, endDate } = dateRange?.[0] || {};
    if (startDate && endDate) tableFilters.push(`from:${toIsoDate(startDate)}`, `to:${toIsoDate(endDate)}`);
    if (activeStatusCard && activeStatusCard !== 'overdue' && activeStatusCard !== 'upcoming') {
      tableFilters.push(`status:'${activeStatusCard}'`);
    }

    tableQueryRef.current = [combinedSearchQuery, ...tableFilters].filter(Boolean).join(' ');
    refreshItems(tableQueryRef.current, page, rowsPerPage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, orderStatusFilter, ppFilter, dateRange, activeStatusCard, page, rowsPerPage]);

  // Deliveries are the full list, so they reload only when their own query changes, not on
  // every page, card or date change
  useEffect(() => {
    const filters = [];
    if (debouncedSearchQuery) filters.push(debouncedSearchQuery);
    if (orderStatusFilter !== 'all') filters.push(`orderStatus:${orderStatusFilter}`);
    if (ppFilter) filters.push(`pp:${ppFilter}`);
    fetchAllItemsForDeliveryAnalytics(filters.join(' '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, orderStatusFilter, ppFilter]);

  // The full-page spinner is only for the first load; later reloads keep the page and show a bar
  const [hasLoaded, setHasLoaded] = useState(false);
  const sawLoadingRef = useRef(false);
  useEffect(() => {
    if (loading) sawLoadingRef.current = true;
    else if (sawLoadingRef.current) setHasLoaded(true);
  }, [loading]);

  // A delete (or an edit that drops a sheet out of the filter) can leave the current page past the end
  useEffect(() => {
    const total = items?.totalElements;
    if (page > 0 && total != null && page * rowsPerPage >= total) {
      setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items?.totalElements]);

  useEffect(() => {
    if (deliveriesToShow.length > 0 && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [deliveriesToShow]);

  const { can } = usePermissions();
  const canEditDelete = can(PERMISSIONS.COSTSHEET_MANAGE_ALL);

   useEffect(() => {
          refreshLegalClientsAndVendors();
      }, [refreshLegalClientsAndVendors]); 

const sendAuthenticatedRequest = useCallback(async (url, method, body) => {
  const token = await getApiToken(instance, account);
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.headers.get('content-type')?.includes('application/json') ? res.json() : res.text();
}, [instance, account]);

  // 
  useEffect(() => {
  if (!account || !canEditDelete) return;
  (async () => {
    setCatalogLoading(true);
    try {
      const [brandsData, catsData] = await Promise.all([
        sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/brands`, 'GET'),
        sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/categories`, 'GET'),
      ]);
      setBrands(brandsData || []);
      setCategories(catsData || []);
      // Every category's sub-categories in one request, rather than one request per category
      const subsByCategory = await sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/catalog/subcategories-by-category`, 'GET');
      const map = {};
      for (const cat of catsData) map[cat.id] = subsByCategory?.[cat.id] || [];
      setCategorySubcategoryMap(map);
    } catch (e) { console.error(e); }
    finally { setCatalogLoading(false); }
  })();
}, [account, canEditDelete, sendAuthenticatedRequest]);

useEffect(() => {
  if (!showEditModal) return;
  const customPP = (editItem.customRows || []).reduce(
    (s, r) => s + (parseFloat(r.customTotalPp) || 0), 0
  );
  const customSP = (editItem.customRows || []).reduce(
    (s, r) => s + (parseFloat(r.customTotalSp) || 0), 0
  );
  const tPP = editItem.items.reduce((s, i) => s + (parseFloat(i.totalPp) || 0), 0) +
    (parseFloat(editItem.transportationTotalPp) || 0) + (parseFloat(editItem.customizationTotalPp) || 0) + (parseFloat(editItem.marketingTotalPp) || 0) + customPP;
  const tSP = editItem.items.reduce((s, i) => s + (parseFloat(i.totalSp) || 0), 0) +
    (parseFloat(editItem.transportationTotalSp) || 0) + (parseFloat(editItem.customizationTotalSp) || 0) + (parseFloat(editItem.marketingTotalSp) || 0) + customSP;
  const mg = tSP - tPP;
  setEditItem(prev => ({ ...prev, totalPP: tPP.toFixed(2), totalSP: tSP.toFixed(2), totalMargin: mg.toFixed(2), totalMarginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0' }));

}, [editItem.items, editItem.transportationTotalPp, editItem.transportationTotalSp, editItem.customizationTotalPp, editItem.customizationTotalSp, editItem.marketingTotalPp, editItem.marketingTotalSp, showEditModal, editItem.customRows,]);

const handleAdminEditClick = (item) => {
  const standardTerms = ["advance", "against-delivery", "10-days", "15-days", "30-days", "45-days", "60-days"];
  const customTerms = {};
  const cs = item.costSummary || {};
  const prepared = {
    ...initialEditState, ...item,
    transportationGST: cs.transportationGST?.toString() || '0',
    transportationTotalPp: item.transportationTotalPp?.toString() || '0',
    transportationTotalSp: item.transportationTotalSp?.toString() || '0',
    customizationGST: cs.customizationGST?.toString()||'0',
    customizationTotalPp:  item.customizationTotalPp?.toString()  || '0',
    customizationTotalSp:  item.customizationTotalSp?.toString()  || '0',
    marketingGST: cs.marketingGST?.toString() || '0',
    marketingTotalPp:      item.marketingTotalPp?.toString()      || '0',
    marketingTotalSp:      item.marketingTotalSp?.toString()      || '0',
    customRows: item.customRows || [],
    items: item.items.map((sub, i) => {
      const pp = parseFloat(sub.pp) || 0, sp = parseFloat(sub.sp) || 0;
      const gst = parseFloat(sub.gst) || 0, qty = parseInt(sub.qty) || 0;
      const nlc = pp * (1 + gst / 100), nsp = sp * (1 + gst / 100);
      const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
      let terms = sub.vendorPaymentTerms;
      if (!standardTerms.includes(terms)) { customTerms[i] = terms; terms = 'custom'; }
      return {
        ...sub, qty: sub.qty?.toString() || '0', gst: sub.gst?.toString() || '0',
        pp: sub.pp?.toString() || '0', sp: sub.sp?.toString() || '0',
        nlc: nlc.toFixed(2), nsp: nsp.toFixed(2),
        totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2),
        margin: mg.toFixed(2), marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0',
        totPPGST: (nlc * qty).toFixed(2), totSPGST: (nsp * qty).toFixed(2),
        vendorPaymentTerms: terms,
        brand: sub.brand || null, category: sub.category || null, subcategory: sub.subcategory || null,
        legalVendorId:
          (sub.legalVendorId == null && sub.vendorName)
            ? 'custom'
            : sub.legalVendorId,
      };
    }),
  };
  setCustomVendorPaymentTerms(customTerms);
  setOriginalCustomTerms(customTerms);
  setEditItem(prepared);
  setOriginalEditItem(JSON.parse(JSON.stringify(prepared)));
  setShowEditModal(true);
};

const handleAdminDelete = async (item) => {
  if (!item?.id) return;
  if (!window.confirm(`Delete cost sheet ${item.salesOrderNo}?`)) return;
  try {
    await sendAuthenticatedRequest(
      `${urlconfig.backend_api_base_url}/api/v2/delete-costsheet/${item.id}`, 'DELETE'
    );
    reloadTable(); // ← no email sent
    setToast(`Cost sheet ${item.salesOrderNo} deleted`);
  } catch (e) { alert(`Error: ${e.message}`); }
};

const handleEditTopLevel = (field, value) => {
  setEditItem(prev => ({
    ...prev, [field]: value,
    // Moving into or out of Internal starts each line's product afresh
    ...(field === 'costsheetfor' && isInternalSwitch(prev.costsheetfor, value) ? { items: (prev.items || []).map(i => ({ ...i, details: '', customDetails: '', esin: '', ecomProductName: '', brand: null, category: null, subcategory: null })) } : {}),
  }));
};

const handleEditItemChange = (index, field, value) => {
  setEditItem(prev => ({
    ...prev,
    items: prev.items.map((item, idx) => {
      if (idx !== index) return item;
      const u = { ...item, [field]: value };
      if (field === 'category') u.subcategory = null;
      const pp = parseFloat(u.pp) || 0, sp = parseFloat(u.sp) || 0;
      const gst = parseFloat(u.gst) || 0, qty = parseInt(u.qty) || 0;
      const nlc = pp * (1 + gst / 100), nsp = sp * (1 + gst / 100);
      const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
      return { ...u, nlc: nlc.toFixed(2), nsp: nsp.toFixed(2), totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2), margin: mg.toFixed(2), marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0', totPPGST: (nlc * qty).toFixed(2), totSPGST: (nsp * qty).toFixed(2) };
    }),
  }));
};

// Totals are recomputed by the effect above as soon as the modal opens, so they are
// left out — otherwise the form looks edited before the user touches anything.
const withoutDerivedTotals = (o) => {
  if (!o) return o;
  const { totalPP, totalSP, totalMargin, totalMarginPercent, ...rest } = o;
  return rest;
};
const hasEditChanges = () =>
  JSON.stringify(withoutDerivedTotals(originalEditItem)) !== JSON.stringify(withoutDerivedTotals(editItem)) ||
  JSON.stringify(originalCustomTerms) !== JSON.stringify(customVendorPaymentTerms);

const addEditItem = () => setEditItem(prev => ({ ...prev, items: [...prev.items, { ...initialEditState.items[0] }] }));
const removeEditItem = (i) => {
  if (editItem.items.length <= 1) { alert('At least one item is required.'); return; }
  setEditItem(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
};

const confirmAdminSave = async () => {
  setShowConfirmModal(false);
  try {
    const itemsPayload = editItem.items.map((item, i) => ({
      ...item,
      legalVendorId: item.legalVendorId === 'custom' ? null : item.legalVendorId,
      vendorName: item.legalVendorId === 'custom' ? (item.vendorName?.trim() || null) : item.vendorName,
      qty: parseInt(item.qty) || 0, gst: parseFloat(item.gst) || 0,
      pp: parseFloat(item.pp) || 0, sp: parseFloat(item.sp) || 0,
      marginPercent: parseFloat(item.marginPercent) || 0,
      vendorPaymentTerms: item.vendorPaymentTerms === 'custom' ? customVendorPaymentTerms[i] : item.vendorPaymentTerms,
      brand:       item.brand       ? { id: item.brand.id }       : null,
      category:    item.category    ? { id: item.category.id }    : null,
      subcategory: item.subcategory ? { id: item.subcategory.id } : null,
    }));

    const payload = {
      ...editItem, items: itemsPayload,
      customRows: editItem.customRows || [],
      // ── Nest GST into costSummary ──
      costSummary: {
        transportationGST: parseFloat(editItem.transportationGST) || 0,
        customizationGST:  parseFloat(editItem.customizationGST)  || 0,
        marketingGST:      parseFloat(editItem.marketingGST)      || 0,
      },
      transportationTotalPp: parseFloat(editItem.transportationTotalPp) || 0,
      transportationTotalSp: parseFloat(editItem.transportationTotalSp) || 0,
      customizationTotalPp:  parseFloat(editItem.customizationTotalPp)  || 0,
      customizationTotalSp:  parseFloat(editItem.customizationTotalSp)  || 0,
      marketingTotalPp:      parseFloat(editItem.marketingTotalPp)      || 0,
      marketingTotalSp:      parseFloat(editItem.marketingTotalSp)      || 0,
    };

    await sendAuthenticatedRequest(
      `${urlconfig.backend_api_base_url}/api/v2/edit-costsheet/${editItem.id}`, 'PUT', payload
    );
    reloadTable();
    setShowEditModal(false);
    setToast(editItem.salesOrderNo ? `Cost sheet ${editItem.salesOrderNo} saved` : 'Cost sheet saved');
  } catch (e) { alert(`Failed: ${e.message}`); }
};

  const isInternal = selectedItem?.costsheetfor?.toLowerCase() === 'internal';

  // ── Delivery card counts (OPTIMIZED) ──
  const cardCounts = useMemo(() => {
    let overdue = 0, upcoming = 0, delivered = 0, posReleased = 0,
        materialInward = 0, purchaseInvoice = 0, branding = 0,
        dispatched = 0, invoiceSent = 0;

    const currentMs = currentDate.getTime();

    for (let i = 0; i < itemsArray.length; i++) {
      const item = itemsArray[i];
      const status = item.orderStatus;

      if (status === 'Delivered') delivered++;
      else if (status === "PO's Released to vendors") posReleased++;
      else if (status === 'Material Inward Done') materialInward++;
      else if (status === 'Purchase Invoice Received') purchaseInvoice++;
      else if (status === 'Branding Completed') branding++;
      else if (status === 'Material Dispatched') dispatched++;
      else if (status === 'Invoice Sent to client') invoiceSent++;

      if (item.deliveryDate) {
        const d = new Date(item.deliveryDate).getTime();
        const diffDays = (d - currentMs) / 86400000;

        if (diffDays < 0 && status === 'Pending') {
          overdue++;
        } else if (diffDays >= 0 && diffDays < 5 && status !== 'Delivered') {
          upcoming++;
        }
      }
    }

    return {
      overdue, upcoming, delivered, posReleased,
      materialInward, purchaseInvoice, branding,
      dispatched, invoiceSent
    };
  }, [itemsArray]);

  const soCardCounts = useMemo(() => {
    let overdue = 0, pending = 0, delivered = 0, posReleased = 0,
        materialInward = 0, purchaseInvoice = 0, branding = 0,
        dispatched = 0, invoiceSent = 0, totalSOs= 0, onHold=0

    const currentMs = currentDate.getTime();

    for (let i = 0; i < filteredByDate.length; i++) {
      const item = filteredByDate[i];
      const status = item.orderStatus;
      //Overdue
      const deliveryTime = item.deliveryDate
      ? new Date(item.deliveryDate).getTime()
      : null;

      if (status === 'Delivered') delivered++;
      else if (status === "PO's Released to vendors") posReleased++;
      else if (status === 'Material Inward Done') materialInward++;
      else if (status === 'Purchase Invoice Received') purchaseInvoice++;
      else if (status === 'Branding Completed') branding++;
      else if (status === 'Material Dispatched') dispatched++;
      // else if (status === 'Invoice Sent to client') invoiceSent++;
      else if (status === 'Pending') pending++;
      else if (status === 'On-Hold') onHold++;
      if ( deliveryTime && deliveryTime < currentMs && status !== 'Delivered') {
        overdue++;
      }
    }
    
     const openSOs =
      pending +
      posReleased +
      materialInward +
      purchaseInvoice +
      branding +
      dispatched +
      invoiceSent+
      onHold;

    return {
      overdue, delivered, posReleased, pending, onHold,
      materialInward, purchaseInvoice, branding,
      dispatched, invoiceSent,totalSOs, openSOs
    };
  }, [filteredByDate]);


  const getDeliveriesByOffset = (offset, dir) => {
    const data = activeTab === 'soAnalytics' ? filteredByDate : itemsArray;
    return data.filter(item => {
      const deliveryDate = new Date(item.deliveryDate);
      const diffDays = (deliveryDate - currentDate) / (1000 * 60 * 60 * 24);
      if (dir === 'next') return diffDays >= offset && diffDays < offset + 5 && item.orderStatus !== 'Delivered';
      else return diffDays <= -offset && diffDays > -(offset + 5) && item.orderStatus !== 'Delivered';
    }).sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate));
  };

  const handleCardClick = (statusKey) => {
    if (activeStatusCard === statusKey) {
      setActiveStatusCard(null);
      setPage(0);
      return;
    }
    setActiveStatusCard(statusKey);
    setPage(0);

    const data = activeTab === 'soAnalytics' ? filteredByDate : itemsArray;

    if (statusKey === 'overdue') {
      showPastOverdue();
    } else if (statusKey === 'upcoming') {
      showNextDeliveries();
    } 
     else if (statusKey === 'Pending') {
      setDeliveriesToShow(
        data.filter(item => item.orderStatus === 'Pending')
        .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate))
      );
      setTableTitle('Pending - Sales Orders');
      setTableColor('orange');
    }else if (statusKey === 'On-Hold') {
        setDeliveriesToShow(
          data.filter(item => item.orderStatus === 'On-Hold')
          .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate))
        );
        setTableTitle('On Hold - Sales Orders');
        setTableColor('orange');
      }
    else if (statusKey === 'Open SOs') {
      setDeliveriesToShow(
        data.filter(item => item.orderStatus !== 'Delivered' )
        .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate))
      );
      setTableTitle('Open SOs - Sales Orders');
      setTableColor('orange');
    }
    else if (statusKey=== 'Overdue')  {
      setDeliveriesToShow(
        data.filter(item => {
          const d = new Date(item.deliveryDate);
          return d < currentDate && item.orderStatus !== 'Delivered';
        })
        .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate))
      );
      setTableTitle('Overdue SOs');
      setTableColor('red');
    }
    else {
      setDeliveriesToShow(data.filter(item => item.orderStatus?.toLowerCase() === statusKey.toLowerCase())
      .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate))
    );
      setTableTitle(`${statusKey} - Sales Orders`);
      setTableColor('blue');
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  };

  const showNextDeliveries = () => {
    setDirection('next');
    setStartOffset(0);
    const deliveries = getDeliveriesByOffset(0, 'next');
    setDeliveriesToShow(deliveries);
    setTableTitle('Upcoming Deliveries - Sales Orders');
    setTableColor('green');
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  };

  const showPastOverdue = () => {
    if (tableTitle === 'Past Overdue Deliveries') return;
    const data = activeTab === 'soAnalytics' ? filteredByDate : itemsArray
    const overdueDeliveries = data
      .filter(item => { const d = new Date(item.deliveryDate); return d < currentDate && item.orderStatus !== 'Delivered'; })
      .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate));
    setDeliveriesToShow(overdueDeliveries);
    setTableTitle('Overdue Deliveries');
    setTableColor('red');
    setDirection('past');
    setStartOffset(0);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  };

  const handleNext = () => {
    if (tableTitle === 'Past Overdue Deliveries') return;
    const newOffset = startOffset + 5;
    setStartOffset(newOffset);
    setDeliveriesToShow(getDeliveriesByOffset(newOffset, direction));
  };

  const handlePrev = () => {
    if (tableTitle === 'Past Overdue Deliveries') return;
    const newOffset = Math.max(0, startOffset - 5);
    setStartOffset(newOffset);
    setDeliveriesToShow(getDeliveriesByOffset(newOffset, direction));
  };

  const clearDateFilter = () => {
    setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setPage(0);
    setDateModal(false);
  };

  const handleViewModal = (item) => { setSelectedItem([item]); setShowModal(true); };
  const handleCloseModal = () => { setShowModal(false); setSelectedItem(null); setDateModal(false); };

  
  const handleOrderStatusFilterChange = (e) => { setOrderStatusFilter(e.target.value); setPage(0); };
  const handlePPFilterChange = (e) => { setPpFilter(e.target.value); setPage(0); };


  const isAnyFilterAppliedForClear = () => {
    const dateFilterActive = dateRange?.[0]?.startDate !== null && dateRange?.[0]?.endDate !== null;
    return dateFilterActive || debouncedSearchQuery.trim() !== '' || orderStatusFilter !== 'all' || ppFilter.trim() !== '' || !!activeStatusCard;
  };

  const clearFilters = () => {
    setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setDebouncedSearchQuery('');
    setOrderStatusFilter('all');
    setPpFilter('');
    setActiveStatusCard(null);
    setPage(0);
  };
  const FilterControls = useMemo(() => (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap' }}>
      <SearchInput
      placeholder="Search orders, clients, SPOC..."
      onSearch={(val) => {
        setDebouncedSearchQuery(val);
        setPage(0);
      }}
      onClear={() => {
        setDebouncedSearchQuery('');
        setPage(0);
      }}
    />
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>PP Filter</InputLabel>
        <Select value={ppFilter} label="PP Filter" onChange={handlePPFilterChange}>
          {PP_RANGES.map(r => (
            <MenuItem key={r.value} value={r.value}>
              <em style={{ fontStyle: 'normal' }}>{r.label}</em>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Order Status</InputLabel>
        <Select value={orderStatusFilter} label="Order Status Filter" onChange={handleOrderStatusFilterChange}>
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="on-hold">On Hold</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
        </Select>
      </FormControl>
      <Badge badgeContent={dateRange[0].startDate ? 1 : 0} color="primary">
        <Button variant="outlined" size="medium" startIcon={<CalendarTodayIcon fontSize="small" />} onClick={() => setDateModal(true)}
          sx={{ textTransform: 'none', color: dateRange[0].startDate ? 'primary.main' : 'text.secondary' }}>
          {dateRange[0].startDate ? `${dateRange[0].startDate.toLocaleDateString()} — ${dateRange[0].endDate?.toLocaleDateString()}` : 'Date Range'}
        </Button>
      </Badge>

      {isAnyFilterAppliedForClear() && (
        <Button size="small" startIcon={<DeleteForeverIcon />} onClick={clearFilters} color="warning" variant="outlined" sx={{ textTransform: 'none' }}>
          Clear Filters
        </Button>
      )}

      <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
        <ExportAlldata />
        <ExportByDate />
      </Box>
    </Box>
  ));
  if (loading && !hasLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress /><Typography sx={{ ml: 2 }}>Loading Admin Data…</Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ textAlign: 'center', mt: 4 }}>{error}</Alert>;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };


  // Every filter is applied by the server, so this is the current page of matching cost sheets
  const filteredContent = items.content || [];

  const aggTotalPP = filteredContent.reduce((acc, item) => {
    const customPP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
    return acc
      + (item.items?.reduce((a, s) => a + s.qty * s.pp, 0) || 0)
      + (item.transportationTotalPp || 0) + (item.customizationTotalPp || 0)
      + (item.marketingTotalPp || 0) + customPP;
  }, 0);

  const aggTotalSP = filteredContent.reduce((acc, item) => {
    const customSP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
    return acc
      + (item.items?.reduce((a, s) => a + s.qty * s.sp, 0) || 0)
      + (item.transportationTotalSp || 0) + (item.customizationTotalSp || 0)
      + (item.marketingTotalSp || 0) + customSP;
  }, 0);

  const aggMargin = aggTotalSP - aggTotalPP;
  const aggMarginPct = aggTotalPP > 0 ? ((aggMargin / aggTotalPP) * 100).toFixed(1) : '0.0';

  // ─── KPI Data (Analytics Style) ────────────────────────────────────────────────
  const kpiData = [
    { key: 'overdue', label: 'Overdue Deliveries', count: cardCounts.overdue, icon: <WarningAmberIcon/>, colorHex: '#dc2626', bg: 'linear-gradient(135deg,#dc2626,#f87171)' },
    { key: 'upcoming', label: 'Next 5 Days', count: cardCounts.upcoming, icon: <LocalShippingIcon/>, colorHex: '#15803d', bg: 'linear-gradient(135deg,#15803d,#4ade80)' },
    { key: 'Delivered', label: 'Delivered', count: cardCounts.delivered, icon: <CheckCircleOutlineIcon/>, colorHex: '#0284c7', bg: 'linear-gradient(135deg,#0284c7,#38bdf8)' },
    { key: "PO's Released to vendors", label: "PO's Released", count: cardCounts.posReleased, icon: <ShoppingCartCheckoutIcon/>, colorHex: '#65a30d', bg: 'linear-gradient(135deg,#65a30d,#a3e635)' },
    { key: 'Material Inward Done', label: 'Material Inward', count: cardCounts.materialInward, icon: <Inventory2Icon/>, colorHex: '#ea580c', bg: 'linear-gradient(135deg,#ea580c,#facc15)' },
    { key: 'Purchase Invoice Received', label: 'Purchase Invoice', count: cardCounts.purchaseInvoice, icon: <ReceiptIcon/>, colorHex: '#4b5563', bg: 'linear-gradient(135deg,#4b5563,#9ca3af)' },
    { key: 'Branding Completed', label: 'Branding Completed', count: cardCounts.branding, icon: <BrushIcon/>, colorHex: '#4f46e5', bg: 'linear-gradient(135deg,#4f46e5,#818cf8)' },
    { key: 'Material Dispatched', label: 'Material Dispatched', count: cardCounts.dispatched, icon: <SendIcon/>, colorHex: '#e11d48', bg: 'linear-gradient(135deg,#e11d48,#fb7185)' },
    { key: 'Invoice Sent to client', label: 'Invoice Sent', count: cardCounts.invoiceSent, icon: <EmailIcon/>, colorHex: '#0d9488', bg: 'linear-gradient(135deg,#0d9488,#2dd4bf)' },
  ];

    const totalSosCount = filteredByDate?.length > 0
      ? filteredByDate.length
      : itemsArray?.length || 0;

    const soAnalyticsKpiData = [
      { key: 'Total SOs', label: 'TotalSos', count: totalSosCount, icon: <AssignmentIcon/>, colorHex: '#94720d', bg: 'linear-gradient(135deg,#0d9488,#2dd4bf)' },
      { key: 'Open SOs',label: 'Open SOs',count: soCardCounts.openSOs, icon: <AssignmentTurnedInIcon />,colorHex: '#f97316',bg: 'linear-gradient(135deg,#f97316,#fdba74)'},
      { key: 'Pending', label: 'Pending', count: soCardCounts.pending, icon: <WarningAmberIcon/>, colorHex: '#dc2626', bg: 'linear-gradient(135deg,#dc2626,#f87171)' },
      { key: "PO's Released to vendors", label: "PO's Released", count: soCardCounts.posReleased, icon: <ShoppingCartCheckoutIcon/>, colorHex: '#65a30d', bg: 'linear-gradient(135deg,#65a30d,#a3e635)' },
      { key: 'Material Inward Done', label: 'Material Inward', count: soCardCounts.materialInward, icon: <Inventory2Icon/>, colorHex: '#ea580c', bg: 'linear-gradient(135deg,#ea580c,#facc15)' },
      { key: 'Purchase Invoice Received', label: 'Purchase Invoice', count: soCardCounts.purchaseInvoice, icon: <ReceiptIcon/>, colorHex: '#4b5563', bg: 'linear-gradient(135deg,#4b5563,#9ca3af)' },
      { key: 'Branding Completed', label: 'Branding Completed', count: soCardCounts.branding, icon: <BrushIcon/>, colorHex: '#4f46e5', bg: 'linear-gradient(135deg,#4f46e5,#818cf8)' },
      { key: 'Material Dispatched', label: 'Material Dispatched', count: soCardCounts.dispatched, icon: <SendIcon/>, colorHex: '#e11d48', bg: 'linear-gradient(135deg,#e11d48,#fb7185)' },
      // { key: 'Invoice Sent to client', label: 'Invoice Sent', count: soCardCounts.invoiceSent, icon: <EmailIcon/>, colorHex: '#0d9488', bg: 'linear-gradient(135deg,#0d9488,#2dd4bf)' },
      { key: 'On-Hold',label: 'On Hold',count: soCardCounts.onHold,icon: <PauseCircleOutlineIcon />,colorHex: '#ca8a04',bg: 'linear-gradient(135deg,#ca8a04,#facc15)'},
      { key: 'Delivered', label: 'Delivered', count: soCardCounts.delivered, icon: <CheckCircleOutlineIcon/>, colorHex: '#0284c7', bg: 'linear-gradient(135deg,#0284c7,#38bdf8)' },
      { key: 'Overdue', label: 'Overdue', count: soCardCounts.overdue, icon: <WarningAmberIcon />, colorHex: '#dc2626', bg: 'linear-gradient(135deg,#dc2626,#f87171)' }
  ];

  // ── Column definitions for toggle ─────────────────────────────────────────
  const COL_KEY_MAP = {
    'Date':            item => <TableCell key="Date">{new Date(item.date).toLocaleDateString('en-GB')}</TableCell>,
    'Creation Date':   item => <TableCell key="CreationDate">{item.createdAT ? new Date(item.createdAT).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'N/A'}</TableCell>,
    'Cost Sheet For':  item => <TableCell key="CSF">{item.costsheetfor}</TableCell>,
    'Client Name':     item => <TableCell key="Client" sx={{ fontWeight: 600 }}>{item.clientRegistrations?.[0]?.legalClientName || item.clientName}</TableCell>,
    'Sales Order No':  item => <TableCell key="SO" sx={{ fontFamily: 'monospace', fontSize: 12, color: '#6366f1' }}>{item.salesOrderNo}</TableCell>,
    'SCM SPOC':        item => <TableCell key="SCM">{item.scmspoc}</TableCell>,
    'Sales SPOC':      item => <TableCell key="SalesSPOC">{item.salesPOC}</TableCell>,
    'Details':         item => <TableCell key="Details">{item.items?.map(i => i.details).join(', ')}</TableCell>,
    'Brand':           item => <TableCell key="Brand">{item.items.map(i => i.brand?.name || 'N/A').join(', ')}</TableCell>,
    'Category':        item => <TableCell key="Cat">{item.items.map(i => i.category?.name || 'N/A').join(', ')}</TableCell>,
    'Subcategory':     item => <TableCell key="Sub">{item.items.map(i => i.subcategory?.name || 'N/A').join(', ')}</TableCell>,
    'Delivery Date':   item => {
      const d = new Date(item.deliveryDate);
      const isInvalid = isNaN(d.getTime()) || d.getFullYear() <= 1971;
      const diff = (d - currentDate) / 86400000;
      return (
        <TableCell key="Del">
          <MuiTooltip title={isInvalid ? '⚠ Invalid date — likely a null stored as epoch 0' : diff < 0 ? `${Math.abs(Math.floor(diff))}d overdue` : `${Math.floor(diff)}d away`} arrow>
            <Typography sx={{
              bgcolor: isInvalid ? '#fef2f2' : 'warning.light',
              color: isInvalid ? '#ef4444' : 'text.primary',
              p: '2px 8px', borderRadius: 1, fontSize: 13, fontWeight: isInvalid ? 700 : 400,
              display: 'inline-block', cursor: 'default',
            }}>
              {isInvalid ? '⚠ Invalid' : d.toLocaleDateString('en-GB')}
            </Typography>
          </MuiTooltip>
        </TableCell>
      );
    },
    'Margin': item => {
      const customPP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
      const customSP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
      const pp = (item.items?.reduce((a, s) => a + s.qty * s.pp, 0) || 0)
        + (item.transportationTotalPp || 0) + (item.customizationTotalPp || 0)
        + (item.marketingTotalPp || 0) + customPP;
      const sp = (item.items?.reduce((a, s) => a + s.qty * s.sp, 0) || 0)
        + (item.transportationTotalSp || 0) + (item.customizationTotalSp || 0)
        + (item.marketingTotalSp || 0) + customSP;
      const mg = sp - pp;
      return (
        <TableCell key="Margin" sx={{ fontWeight: 700, color: mg >= 0 ? '#22c55e' : '#ef4444' }}>
          ₹{formatCurrency(mg)}
        </TableCell>
      );
    },
    'Total PP': item => {
        const customPP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalPp) || 0), 0);
      return(
      <TableCell key="TotalPP" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
        ₹{formatCurrency(item.items?.reduce((a, s) => a + s.qty * s.pp, 0) + (item.transportationTotalPp||0) + (item.customizationTotalPp||0) + (item.marketingTotalPp||0)+ customPP)}
      </TableCell>
    )},
    'Total SP': item => {
      const customSP = (item.customRows || []).reduce((s, r) => s + (parseFloat(r.customTotalSp) || 0), 0);
      return(
      <TableCell key="TotalSP" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
        ₹{formatCurrency(item.items?.reduce((a, s) => a + s.qty * s.sp, 0) + (item.transportationTotalSp||0) + (item.customizationTotalSp||0) + (item.marketingTotalSp||0)+ customSP)}
      </TableCell>
    )},
    'PO Number': item => <TableCell key="PO">{item.items.map(i => i.poNumber).join(', ')}</TableCell>,
    'Created By': item => <TableCell key="CreatedBy">{item.createdBy}</TableCell>,
    'Approval Status': item => (
      <TableCell key="Approval">
        {item.pending === 'true' && item.approvedBy?.length > 0
          ? <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 700 }}>✓ {item.approvedBy.join(', ')}</Typography>
          : item.pending === 'false'
            ? <Box>
                <Typography variant="caption" color="error" display="block">✗ Rejected by {item.approvedBy?.join(', ')}</Typography>
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>Note: {item.remarks}</Typography>
              </Box>
            : <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700 }}>⏳ Pending</Typography>
        }
      </TableCell>
    ),
    'Order Status': item => (
      <TableCell key="Status">
        <Chip
          label={item.orderStatus}
          size="small"
          sx={{
            fontSize: 11, fontWeight: 700,
            color: STATUS_COLORS[item.orderStatus]?.color || '#374151',
            background: STATUS_COLORS[item.orderStatus]?.bg || '#f3f4f6',
            border: `1px solid ${STATUS_COLORS[item.orderStatus]?.color || '#e5e7eb'}33`,
          }}
        />
      </TableCell>
    ),
  };

  const activeFilters = [activeStatusCard !== null, orderStatusFilter !== 'all', !!dateRange[0]?.startDate, ppFilter !== ''].filter(Boolean).length;

  const isEditFormValid = () => {
    if (!editItem.clientName?.trim()) return false;
    if (!editItem.costsheetfor?.trim()) return false;
    if (!editItem.salesPOC?.trim()) return false;
    if (!editItem.scmspoc?.trim()) return false;
    if (!editItem.location?.trim()) return false;
    if (!editItem.orderStatus?.trim()) return false;

    // Every line item must be filled in
    if (!editItem.items || editItem.items.length === 0) return false;
    return editItem.items.every(item => {
      const hasQty = item.qty !== '' && item.qty != null && parseFloat(item.qty) > 0;
      const hasPP = item.pp !== '' && item.pp != null && parseFloat(item.pp) >= 0;
      const hasSP = item.sp !== '' && item.sp != null && parseFloat(item.sp) >= 0;
      const hasVendorName = item.legalVendorId && item.legalVendorId !== 'custom' ? true : (item.vendorName?.trim() !== '' && item.vendorName != null);
      const hasDetails = item.details?.trim();
      return hasQty && hasPP && hasSP && hasDetails && hasVendorName;
    });
  };

  return (
    <DashShell>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;600&display=swap');`}</style>

      {/* ── Header ── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: { xs: 2, md: 4 }, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        {isMobile && (
          <IconButton size="small" onClick={() => setFilterDrawer(true)}>
            <Badge badgeContent={activeFilters} color="error"><MenuIcon /></Badge>
          </IconButton>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={900} letterSpacing="-0.5px">Order Management Admin</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {items.totalElements || 0} Total Records Found
          </Typography>
        </Box>
      </Box>

      {/* ── Desktop Sticky Filter ── */}
      {!isMobile && <StickyFilter elevation={0}>{FilterControls}</StickyFilter>}

      {/* ── Mobile Drawer Filter ── */}
      <Drawer anchor="left" open={filterDrawer} onClose={() => setFilterDrawer(false)}>
        <Box sx={{ width: 320, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography fontWeight={800} variant="h6">Filters & Exports</Typography>
            <IconButton size="small" onClick={() => setFilterDrawer(false)}><CloseIcon /></IconButton>
          </Box>
          <FilterControls />
        </Box>
      </Drawer>

      {/* ── Date Modal ── */}
      <Dialog open={DateModal} onClose={() => setDateModal(false)} maxWidth="sm">
        <DialogTitle fontWeight={800}>Select Date Range</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <DateRange editableDateInputs onChange={item => { setDateRange([item.selection]); setPage(0); }} moveRangeOnFirstSelection={false} ranges={dateRange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={clearDateFilter} color="secondary">Clear</Button>
          <Button variant="contained" onClick={() => setDateModal(false)}>Apply</Button>
        </DialogActions>
      </Dialog>

      <SectionWrap>
        {/* ── Tab switcher ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '2px solid #e5e7eb', pb: 0 }}>
          {[['dashboard', '⊞ Dashboard'], ['overdue', '⚠ Overdue View'], ['soAnalytics', '📊 SO Analytics']].map(([tab, label]) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              disableRipple
              sx={{
                borderRadius: '8px 8px 0 0', px: 2.5, py: 1, fontSize: 13, fontWeight: 700,
                color: activeTab === tab ? '#6366f1' : '#6b7280',
                background: activeTab === tab ? '#eef2ff' : 'transparent',
                borderBottom: activeTab === tab ? '2px solid #6366f1' : '2px solid transparent',
                mb: '-2px',
                '&:hover': { background: '#f3f4f6' },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>

        {activeTab === 'overdue' ? (
          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.5}>Overdue Deliveries</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              All orders past their delivery date, grouped by date. Invalid dates indicate a data entry issue.
            </Typography>
            <OverdueAccordion itemsArray={itemsArray} currentDate={currentDate} />
          </Box>
        ) :activeTab === 'soAnalytics' ? (
          <Box>
            <Typography variant="h6" fontWeight={700}>
              SO Analytics
            </Typography>
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {soAnalyticsKpiData.map((c, i) => {
        const isActive = activeStatusCard === c.key;

        return (
          <Grid item xs={6} sm={4} md={3} lg={2.4} key={c.key}>
            <KpiCard
              onClick={() => handleCardClick(c.key)}
              sx={{
                animationDelay: `${i * 60}ms`,
                border: isActive ? `2px solid ${c.colorHex}` : '2px solid transparent',
                boxShadow: isActive
                  ? `0 8px 24px ${c.colorHex}40`
                  : '0 2px 16px rgba(0,0,0,0.06)',
                transform: isActive ? 'translateY(-3px)' : 'none'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  right: -12,
                  top: -12,
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: c.bg,
                  opacity: 0.12
                }}
              />

              <Typography
                sx={{ fontSize: 26, lineHeight: 1, mb: 0.5, color: c.colorHex }}
              >
                {c.icon}
              </Typography>

              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ lineHeight: 1.1, letterSpacing: '-0.5px' }}
              >
                {c.count}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {c.label}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: isActive ? c.colorHex : '#9ca3af',
                  fontSize: '0.68rem',
                  display: 'block',
                  mt: 0.5
                }}
              >
                {isActive ? 'Filtering table ↓' : 'Click to filter'}
              </Typography>

            </KpiCard>
      </Grid>
        );
      })}
    </Grid>
     {/* ── Deliveries Table + navigation ── */}
            <DeliveriesTable ref={tableRef} deliveries={deliveriesToShow} title={tableTitle} color={tableColor} />
            {deliveriesToShow.length > 0 && tableTitle === 'Upcoming Deliveries - Sales Orders' && (
              <Grid container justifyContent="center" spacing={1} sx={{ mt: 1 }}>
                <Grid item>
                  <IconButton onClick={handlePrev} disabled={startOffset === 0}><ArrowBackIcon /></IconButton>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleNext}><ArrowForwardIcon /></IconButton>
                </Grid>
              </Grid>
            )}
          </Box>
        ) : (
          <>
            {/* ── Animated KPI Cards ── */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {kpiData.map((c, i) => {
                const isActive = activeStatusCard === c.key;
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={c.key}>
                    <KpiCard
                      onClick={() => handleCardClick(c.key)}
                      sx={{
                        animationDelay: `${i * 60}ms`,
                        border: isActive ? `2px solid ${c.colorHex}` : '2px solid transparent',
                        boxShadow: isActive ? `0 8px 24px ${c.colorHex}40` : '0 2px 16px rgba(0,0,0,0.06)',
                        transform: isActive ? 'translateY(-3px)' : 'none'
                      }}
                    >
                      <Box sx={{ position: 'absolute', right: -12, top: -12, width: 72, height: 72, borderRadius: '50%', background: c.bg, opacity: 0.12 }} />
                      <Typography sx={{ fontSize: 26, lineHeight: 1, mb: 0.5, color: c.colorHex }}>{c.icon}</Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: '-0.5px' }}>{c.count}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>{c.label}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: isActive ? c.colorHex : '#9ca3af', fontSize: '0.68rem', display: 'block', mt: 0.5 }}>
                        {isActive ? 'Filtering table ↓' : 'Click to filter'}
                      </Typography>
                    </KpiCard>
                  </Grid>
                );
              })}
            </Grid>

            {loading && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

            {/* ── Aggregate summary bar ── */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              {[
                { label: 'Filtered Orders',               value: (items.totalElements || 0).toLocaleString(), mono: false },
                { label: 'Total PP (this page)',          value: `₹${formatCurrency(aggTotalPP)}`, mono: true },
                { label: 'Total SP (this page)',          value: `₹${formatCurrency(aggTotalSP)}`, mono: true },
                { label: 'Margin (this page)',            value: `₹${formatCurrency(aggMargin)} (${aggMarginPct}%)`, positive: aggMargin >= 0 },
              ].map(({ label, value, mono, positive }) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Paper variant="outlined" sx={{ p: '12px 16px', borderRadius: 2, borderColor: '#e5e7eb' }}>
                    <Typography fontSize={10} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.06em" mb={0.25}>{label}</Typography>
                    <Typography fontSize={20} fontWeight={800} sx={{ fontFamily: mono ? "'DM Mono', monospace" : 'inherit', color: positive === false ? 'error.main' : positive ? '#22c55e' : '#111827' }}>
                      {value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Active filter chips */}
            {(activeStatusCard || ppFilter || orderStatusFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {activeStatusCard && (
                  <Chip
                    label={`Card: ${kpiData.find(c => c.key === activeStatusCard)?.label || activeStatusCard}`}
                    onDelete={() => setActiveStatusCard(null)} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#6366f1' }}
                  />
                )}
                {ppFilter && (
                  <Chip
                    label={`PP: ${PP_RANGES.find(r => r.value === ppFilter)?.label || ppFilter}`}
                    onDelete={() => setPpFilter('')} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, background: '#fff7ed', color: '#f97316' }}
                  />
                )}
                {orderStatusFilter !== 'all' && (
                  <Chip
                    label={`Status: ${orderStatusFilter}`}
                    onDelete={() => setOrderStatusFilter('all')} size="small"
                    sx={{ fontSize: 11, fontWeight: 600, background: '#f0fdf4', color: '#22c55e' }}
                  />
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Scroll horizontally to view more table data.
              </Typography>
              <ColumnToggleMenu visibleCols={visibleCols} setVisibleCols={setVisibleCols} />
            </Box>

            {/* ── Main Table ── */}
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', mb: 4, borderRadius: 3, border: '1px solid #e5e7eb' }}>
              <Table sx={{ minWidth: 900 }} id="DashboardTable" ref={tableRef}>
                <TableHead>
                  <TableRow sx={{ background: '#f8fafc' }}>
                    <TableCell sx={{ display: 'none' }}>Transportation total PP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Transportation total SP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Customization total PP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Customization total SP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Marketing total PP</TableCell>
                    <TableCell sx={{ display: 'none' }}>Marketing total SP</TableCell>
                    {visibleCols.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#374151', whiteSpace: 'nowrap' }}>
                        {col}
                      </TableCell>
                    ))}
                    {canEditDelete && <TableCell>Action</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContent.length > 0 ? (
                    filteredContent
                      .map((item, rowIdx) => {
                        const hasBeenApproved = item.pending === 'true';
                        return (
                          <TableRow
                            key={item.id}
                            onClick={() => handleViewModal(item)}
                            sx={{
                              background: rowIdx % 2 === 0 ? '#fff' : '#fafafa',
                              cursor: 'pointer',
                              '&:hover': { background: '#e0f2fe' },
                              transition: 'all .15s',
                            }}
                          >
                            <TableCell sx={{ display: 'none' }}>{item.transportationTotalPp}</TableCell>
                            <TableCell sx={{ display: 'none' }}>{item.transportationTotalSp}</TableCell>
                            <TableCell sx={{ display: 'none' }}>{item.customizationTotalPp}</TableCell>
                            <TableCell sx={{ display: 'none' }}>{item.customizationTotalSp}</TableCell>
                            <TableCell sx={{ display: 'none' }}>{item.marketingTotalPp}</TableCell>
                            <TableCell sx={{ display: 'none' }}>{item.marketingTotalSp}</TableCell>

                            {visibleCols.map(col => COL_KEY_MAP[col]?.(item))}

                            {canEditDelete && (
                              <TableCell onClick={e => e.stopPropagation()}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Button size="small" variant="contained" onClick={() => handleAdminEditClick(item)}
                                    sx={{ fontSize: 12, textTransform: 'none', bgcolor: '#6366f1' }}>Edit</Button>
                                  <Button size="small" variant="outlined" color="error" onClick={() => handleAdminDelete(item)}
                                    sx={{ fontSize: 12, textTransform: 'none' }}>Delete</Button>
                                </Box>
                              </TableCell>
                            )}
                            
                          </TableRow>
                        );
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={visibleCols.length + 1} align="center" sx={{ py: 8 }}>
                        <Typography fontSize={40} mb={1}>🔍</Typography>
                        <Typography fontWeight={700} fontSize={16} color="#374151" mb={0.5}>No cost sheets found</Typography>
                        <Typography color="text.secondary" fontSize={14} mb={2}>
                          Try adjusting your search query or filters.
                        </Typography>
                        {isAnyFilterAppliedForClear() && (
                          <Button variant="outlined" onClick={clearFilters} sx={{ borderRadius: 2 }}>
                            Clear all filters
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={items.totalElements || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count.toLocaleString() : `more than ${to}`}`}
                sx={{ borderTop: '1px solid #f3f4f6' }}
              />
            </TableContainer>

            {/* ── View Modal ── */}
            <Dialog open={showModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
              <DialogTitle>
                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Typography variant="h6">Sales Order No: {selectedItem?.[0]?.salesOrderNo}</Typography>
                  </Grid>
                  <Grid item><CostSheetHistory selectedItem={selectedItem?.[0]} /></Grid>
                  <Grid item>
                    <Button onClick={() => { if (selectedItem?.[0]) generateCostSheetPdf(selectedItem[0], legalVendors); }}>
                      Print Costsheet
                    </Button>
                  </Grid>
                </Grid>
              </DialogTitle>
              <DialogContent dividers>
                {selectedItem && selectedItem[0] ? (
                  <Box ref={targetRef} sx={{ p: 2 }}>
                    <Typography variant="h5" align="center" gutterBottom>COST SHEET</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        ['Date', selectedItem[0]?.date ? new Date(selectedItem[0].date).toLocaleDateString('en-GB') : 'N/A'],
                        ['Delivery Date', selectedItem[0]?.deliveryDate ? new Date(selectedItem[0].deliveryDate).toLocaleDateString('en-GB') : 'N/A'],
                        ['Created By', selectedItem[0]?.createdBy || 'N/A'],
                        ['Email', selectedItem[0]?.userEmail || 'N/A'],
                        ['Cost Sheet for', selectedItem[0]?.costsheetfor || 'N/A'],
                        ['Client Name', selectedItem[0]?.clientRegistrations?.[0]?.legalClientName || selectedItem[0]?.clientName || 'N/A'],
                        ['Sales Order No', selectedItem[0]?.salesOrderNo || 'N/A'],
                        ['Sales SPOC', selectedItem[0]?.salesPOC || 'N/A'],
                        ['SCM SPOC', selectedItem[0]?.scmspoc || 'N/A'],
                        ['Client Payment Terms', selectedItem[0]?.clientPaymentTerms || 'N/A'],
                        ['Location', selectedItem[0]?.location || 'N/A'],
                        ...(isInternal
                            ? [{ label: 'Internal Category', val: selectedItem[0]?.internalCategory }]
                        : [])
                      ].map(([label, val]) => (
                        <Grid item xs={12} sm={4} key={label}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}:</Typography>
                          <Typography variant="body2">{val}</Typography>
                        </Grid>
                      ))}
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Order Status:</Typography>
                        <Typography variant="body2" sx={{ bgcolor: 'warning.light', color: 'text.primary', p: 0.5, borderRadius: 1, display: 'inline-block' }}>
                          {selectedItem[0]?.orderStatus || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Approval Status:</Typography>
                        <Typography variant="body2">
                          {selectedItem[0]?.pending === undefined || selectedItem[0]?.pending === null || selectedItem[0]?.pending === ''
                            ? 'Pending'
                            : selectedItem[0]?.pending === 'true' || selectedItem[0]?.pending === true
                              ? `Approved By ${selectedItem[0]?.approvedBy?.join(', ') || 'N/A'}`
                              : (
                                <Box component="span">
                                  <Typography component="span" color="error">Rejected By {selectedItem[0]?.approvedBy?.join(', ') || 'N/A'}</Typography>
                                  <br />
                                  <Typography component="span" variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                    Reject Note: {selectedItem[0]?.remarks || 'N/A'}
                                  </Typography>
                                </Box>
                              )
                          }
                        </Typography>
                      </Grid>

                      {!isInternal && (
                      <Grid item xs={12} sm={4}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Internal Category:</Typography>
                        <Typography variant="body2">{selectedItem[0]?.internalCategory || 'N/A'}</Typography>
                      </Grid>
                      )} 
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Cost Sheet Remarks:</Typography>
                        <Typography variant="body2">{selectedItem[0]?.costSheetRemarks || 'N/A'}</Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Order Details</Typography>
                    <LineItemsTable
                          items={(selectedItem[0]?.items || []).map(item => {
                            const pp = parseFloat(item.pp) || 0;
                            const sp = parseFloat(item.sp) || 0;
                            const gst = parseFloat(item.gst) || 0;
                            const qty = parseInt(item.qty) || 0;
                            const nlc = pp * (1 + gst / 100);
                            const nsp = sp * (1 + gst / 100);
                            const tPP = pp * qty;
                            const tSP = sp * qty;
                            return {
                              ...item,
                              nlc: nlc.toFixed(2),
                              nsp: nsp.toFixed(2),
                              totalPp: tPP.toFixed(2),
                              totalSp: tSP.toFixed(2),
                              margin: (tSP - tPP).toFixed(2),
                              marginPercent: tPP !== 0 ? ((tSP - tPP) / tPP * 100).toFixed(1) : '0.0',
                              totPPGST: (nlc * qty).toFixed(2),
                              totSPGST: (nsp * qty).toFixed(2),
                            };
                          })}
                          subtotals={{
                            transportationTotalPp: selectedItem[0]?.transportationTotalPp,
                            transportationTotalSp: selectedItem[0]?.transportationTotalSp,
                              transportationGST: selectedItem[0]?.costSummary?.transportationGST ?? 0,
                            customizationTotalPp: selectedItem[0]?.customizationTotalPp,
                            customizationTotalSp: selectedItem[0]?.customizationTotalSp,
                            customizationGST: selectedItem[0]?.costSummary?.customizationGST ?? 0,
                            marketingTotalPp: selectedItem[0]?.marketingTotalPp,
                            marketingTotalSp: selectedItem[0]?.marketingTotalSp,
                            marketingGST: selectedItem[0]?.costSummary?.marketingGST ?? 0,  // ← costSummary
                          }}
                          customRows={selectedItem[0]?.customRows || []}
                          brands={brands}
                          categories={categories}
                          categorySubcategoryMap={categorySubcategoryMap}
                          viewOnly
                          legalVendors={legalVendors}
                        />
                  </Box>
                ) : (
                  <Typography sx={{ p: 2, textAlign: 'center' }}>No item data to display.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseModal} color="secondary">Close</Button>
              </DialogActions>
            </Dialog>

            

            {/* ── Deliveries Table + navigation ── */}
            <DeliveriesTable ref={tableRef} deliveries={deliveriesToShow} title={tableTitle} color={tableColor} />
            {deliveriesToShow.length > 0 && tableTitle === 'Upcoming Deliveries - Sales Orders' && (
              <Grid container justifyContent="center" spacing={1} sx={{ mt: 1 }}>
                <Grid item>
                  <IconButton onClick={handlePrev} disabled={startOffset === 0}><ArrowBackIcon /></IconButton>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleNext}><ArrowForwardIcon /></IconButton>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </SectionWrap>

      {/* ── ADMIN EDIT DIALOG ── */}
<Dialog open={showEditModal} onClose={() => setShowEditModal(false)} fullScreen>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #e2e8f0' }}>
    <Typography variant="h6" fontWeight={800}>Edit Cost Sheet: {editItem?.salesOrderNo}</Typography>
    <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => setShowEditModal(false)}><CloseIcon /></IconButton>
  </DialogTitle>

  <DialogContent sx={{ p: 3 }}>
    <Paper sx={{ p: 2.5, borderRadius: 2, mb: 2.5, border: '1px solid #e2e8f0' }}>
      <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" color="#6366f1" mb={2}>
        Basic Information
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
                <Autocomplete
                  options={legalClients}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : (o.legalClientName || ''))}
                  value={
                    legalClients.find(c => c.id === editItem.legalClientId)
                    || legalClients.find(c => c.legalClientName === editItem.clientName)
                    || null
                  }
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  onChange={(_, newVal) => {
                    if (newVal) {
                      handleEditTopLevel('clientName', newVal.legalClientName);
                      handleEditTopLevel('legalClientId', newVal.id);
                      handleEditTopLevel('clientGST', newVal.clientGST || '');
                      handleEditTopLevel('clientState', newVal.clientState || '');
                    } else {
                      handleEditTopLevel('clientName', '');
                      handleEditTopLevel('legalClientId', null);
                      handleEditTopLevel('clientGST', '');
                      handleEditTopLevel('clientState', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                      <Typography variant="body2" fontWeight={600} fontSize={13}>{option.legalClientName}</Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
                        {option.clientGST && <Typography variant="caption" sx={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 11 }}>GST: {option.clientGST}</Typography>}
                        {option.clientState && <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 11 }}>{option.clientState}</Typography>}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Client Name" size="small" required />
                  )}
                />
                {editItem.clientGST && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6366f1', fontWeight: 600 }}>
                    GST: {editItem.clientGST} {editItem.clientState ? `· ${editItem.clientState}` : ''}
                  </Typography>
                )}
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Cost Sheet For</InputLabel>
            <Select value={editItem.costsheetfor} label="Cost Sheet For"
              onChange={e => handleEditTopLevel('costsheetfor', e.target.value)}>
              <MenuItem value="eyantraventures">Eyantra Ventures</MenuItem>
              <MenuItem value="Mozato">Mozato</MenuItem>
              <MenuItem value="eYantraIndustries">eYantra Industries</MenuItem>
              <MenuItem value="Sample">Sample</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="inventory">Inventory</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Sales POC"
            value={editItem.salesPOC}
            onChange={e => handleEditTopLevel('salesPOC', e.target.value)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="SCM SPOC"
            value={editItem.scmspoc}
            onChange={e => handleEditTopLevel('scmspoc', e.target.value)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Client Payment Terms"
            disabled={editItem?.costsheetfor?.toLowerCase()==='internal'}
            value={editItem.clientPaymentTerms}
            onChange={e => handleEditTopLevel('clientPaymentTerms', e.target.value)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Location"
            value={editItem.location}
            onChange={e => handleEditTopLevel('location', e.target.value)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Order Status</InputLabel>
            <Select value={editItem.orderStatus} label="Order Status"
              onChange={e => handleEditTopLevel('orderStatus', e.target.value)}>
              {['Pending','On-Hold',"PO's Released to vendors",'Material Inward Done',
                'Purchase Invoice Received','Branding Completed','Material Dispatched','Delivered']
                .map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Paper>

    {/* Line Items — reuse the same LineItemsTable from UserDashboard */}
    <Paper sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ p: '12px 20px', borderLeft: '4px solid #22c55e', borderBottom: '1px solid #e2e8f0' }}>
        <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" color="#22c55e">Line Items</Typography>
      </Box>
      <LineItemsTable
        items={editItem.items}
        onItemChange={handleEditItemChange}
        onAddItem={addEditItem}
        onRemoveItem={removeEditItem}
        isInternal={editItem?.costsheetfor?.toLowerCase() === 'internal'}
        subtotals={{
          transportationGST: editItem.transportationGST,
          transportationTotalPp: editItem.transportationTotalPp,
          transportationTotalSp: editItem.transportationTotalSp,
          customizationGST: editItem.customizationGST,
          customizationTotalPp:  editItem.customizationTotalPp,
          customizationTotalSp:  editItem.customizationTotalSp,
          marketingGST: editItem.marketingGST,
          marketingTotalPp:      editItem.marketingTotalPp,
          marketingTotalSp:      editItem.marketingTotalSp,
        }}
        onSubtotalChange={handleEditTopLevel}
        brands={brands} categories={categories}
        categorySubcategoryMap={categorySubcategoryMap}
        catalogLoading={catalogLoading}
        customVendorTerms={customVendorPaymentTerms}
        onCustomVendorTermChange={(i, v) => setCustomVendorPaymentTerms(prev => ({ ...prev, [i]: v }))}
        qtyErrors={qtyErrors}
        customRows={editItem.customRows || []}
        onCustomRowChange={(updated) =>
          setEditItem(prev => ({ ...prev, customRows: updated }))
        }
        onQtyErrorChange={(i, err) => setQtyErrors(p => ({ ...p, [i]: err }))}
        onOpenCatalog={(i) => { setCatalogTargetIndex(i); setProductModalOpen(true); }}
        legalVendors={legalVendors}
        onLegalVendorChange={(i, v) => handleEditItemChange(i, 'legalVendorId', v?.id || null)}

      />
    </Paper>
  </DialogContent>

  <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
    <Button onClick={() => setShowEditModal(false)} variant="outlined">Cancel</Button>
    <Button variant="contained"
      disabled={!hasEditChanges() || !isEditFormValid()}
      onClick={() => setShowConfirmModal(true)}
      sx={{ bgcolor: '#6366f1', fontWeight: 700, px: 3 }}>
      Save Changes
    </Button>
  </DialogActions>
</Dialog>

{/* ── CONFIRM DIALOG ── */}
<Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
  <DialogTitle fontWeight={800}>Confirm Changes</DialogTitle>
  <DialogContent>
    <Typography>Save these changes? No notifications will be sent.</Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
    <Button onClick={confirmAdminSave} variant="contained" sx={{ bgcolor: '#6366f1' }}>Yes, Save</Button>
  </DialogActions>
</Dialog>
 {/** Product selection modal */}
      <ProductSelectorModal
        open={productModalOpen}
        onClose={() => {
          setProductModalOpen(false);
          setCatalogTargetIndex(null);
        }}
        onSelect={async (product) => {
          if (catalogTargetIndex === null) return;

          const findIn = (list, name) => name ? list.find(i => i.name?.toLowerCase() === name.toLowerCase()) || null : null;
          const ensure = async (type, name, parentId = null) => {
            if (!name) return null;
            try {
              return await sendAuthenticatedRequest(
                `${urlconfig.backend_api_base_url}/api/v2/catalog/ensure-${type}`,
                'POST',
                parentId ? { name, categoryId: parentId } : { name }
              );
            } catch (e) {
              console.error(`Failed to ensure ${type}:`, e);
              return { name };
            }
          };

          let tBrand = product.brandName ? findIn(brands, product.brandName) : null;
          if (product.brandName && !tBrand) {
            tBrand = await ensure('brand', product.brandName);
            if (tBrand) setBrands(p => [...p, tBrand]);
          }

          let tCat = findIn(categories, product.omsCategory);
          if (!tCat && product.omsCategory) {
            tCat = await ensure('category', product.omsCategory);
            if (tCat) {
              setCategories(p => [...p, tCat]);
              setCategorySubcategoryMap(p => ({ ...p, [tCat.id || tCat.name]: [] }));
            }
          }

          let tSub = null;
          if (tCat && product.omsSubcategory) {
            const key = tCat.id || tCat.name;
            tSub = findIn(categorySubcategoryMap[key] || [], product.omsSubcategory);
            if (!tSub) {
              tSub = await ensure('subcategory', product.omsSubcategory, tCat.id);
              if (tSub) setCategorySubcategoryMap(p => ({ ...p, [key]: [...(p[key] || []), tSub] }));
            }
          }

          setEditItem(prev => ({
            ...prev,
            items: prev.items.map((item, idx) => {
              if (idx !== catalogTargetIndex) return item;
              const u = {
                ...item,
                details: product.productName || '',
                esin: product.ecomProductId?.toString() || '',
                ecomProductName: product.productName || '',
                vendorName: product.lastVendor || '',
                pp: (product.ecomCostPrice ?? '').toString(),
                sp: (product.ecomMrp ?? '').toString(),
                gst: (product.ecomGst ?? '').toString(),
                brand: tBrand || null,
                category: tCat || null,
                subcategory: tSub || null,
              };
              const pp = parseFloat(u.pp) || 0;
              const sp = parseFloat(u.sp) || 0;
              const gst = parseFloat(u.gst) || 0;
              const qty = parseInt(u.qty) || 0;
              const nlc = pp * (1 + gst / 100);
              const nsp = sp * (1 + gst / 100);
              const tPP = pp * qty, tSP = sp * qty, mg = tSP - tPP;
              return {
                ...u,
                nlc: nlc.toFixed(2), nsp: nsp.toFixed(2),
                totalPp: tPP.toFixed(2), totalSp: tSP.toFixed(2),
                margin: mg.toFixed(2),
                marginPercent: tPP !== 0 ? ((mg / tPP) * 100).toFixed(1) : '0.0',
                totPPGST: (nlc * qty).toFixed(2),
                totSPGST: (nsp * qty).toFixed(2),
              };
            }),
          }));

          setProductModalOpen(false);
          setCatalogTargetIndex(null);
        }}
        baseUrl={urlconfig.backend_api_base_url}
      />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setToast('')} severity="success" variant="filled" sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </DashShell>

    
  );
};

export default AdminDashboard;